/**
 * Debounces a function call, limiting how often it can be executed
 * @param {function} func function to be called after timeout
 * @param {int} wait ms to wait
 * @param {boolean} immediate perform immediately or in next countdown
 */
export function debounce(func, wait = 10, immediate = true) {
  let timeout;
  const debounced = function () {
    let context = this,
      args = arguments;
    let later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
    timeout = null;
  };
  return debounced;
}

/**
 * Toggle zoom
 */
export function toggleZoom() {
  const zoomWrapper = document.getElementById('vue-zoom-wrapper');
  if (!zoomWrapper) return;
  const isOpening = zoomWrapper.classList.contains('hideZoom');
  if (isOpening) {
    zoomWrapper.classList.remove('hideZoom');
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('pdp-zoom:open', { bubbles: true }));
  } else {
    zoomWrapper.classList.add('hideZoom');
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('pdp-zoom:close', { bubbles: true }));
  }
}

export function getMediaAlt(media) {
  const node = media?.node || media || {};
  return node.alt || node.altText || node.image?.alt || node.image?.altText || '';
}

/**
 * Strip Shopify Product GID prefix so PLP payloads (gid) match PDP query params (numeric id)
 * @param {string|number} value
 * @returns {string}
 */
export function normalizeShopifyProductId(value) {
  if (value == null || value === '') return '';
  return String(value).replace('gid://shopify/Product/', '');
}

export function normalizeShopifyCollectionId(value) {
  if (value == null || value === '') return '';
  return String(value).replace('gid://shopify/Collection/', '');
}

export function productRowMatchesShopifyProductId(product, rawWant) {
  const want = normalizeShopifyProductId(rawWant);
  if (!want) return false;
  const candidates = [];
  const add = v => {
    const n = normalizeShopifyProductId(v);
    if (n) candidates.push(n);
  };
  add(product?.id);
  add(product?.pid);
  add(product?.i);
  add(product?.productID);
  add(product?.product_id);
  if (Array.isArray(product?.alt)) {
    for (const row of product.alt) {
      if (Array.isArray(row) && row.length > 2) add(row[2]);
    }
  }
  return candidates.some(c => c === want);
}

/**
 * Get product category metafield one from FS response
 * @param {Array} productList array of products from FastSimon
 * @param {int} index index of the product
 * @param {string} metafieldStr metafield string
 */
export function getProductMetafieldFromFS(productList, index, metafieldStr) {
  let metafieldOne = '';
  if (index !== undefined && productList[index] !== undefined && productList[index].att !== undefined) {
    const productAttributes = Array.from(productList[index].att);
    productAttributes.forEach(attribute => {
      // If it contains a certain metafield
      if (metafieldStr !== undefined && metafieldStr !== 'undefined' && metafieldStr !== '' && attribute.indexOf(metafieldStr) > -1) {
        metafieldOne = attribute?.[1]?.[0];
      }
    });
  }
  return metafieldOne;
}

/**
 * Get product collection metafield from FS response
 * Extracts collection URL, handle, or GID from collection_reference metafields
 * @param {Array} productList array of products from FastSimon
 * @param {int} index index of the product
 * @param {string} metafieldStr metafield string (namespace.key format)
 * @returns {string} Collection URL, handle, or GID
 */
export function getProductCollectionMetafieldFromFS(productList, index, metafieldStr) {
  let collectionData = null;
  if (index !== undefined && productList[index] !== undefined && productList[index].att !== undefined) {
    const productAttributes = Array.from(productList[index].att);
    for (const attribute of productAttributes) {
      if (metafieldStr !== undefined && metafieldStr !== 'undefined' && metafieldStr !== '' && attribute.indexOf(metafieldStr) > -1) {
        collectionData = attribute?.[1]?.[0];
        break;
      }
    }
  }
  if (!collectionData) return '';
  if (typeof collectionData === 'string') {
    if (collectionData.startsWith('/collections/') || collectionData.startsWith('http')) {
      return collectionData;
    }
    return collectionData;
  }
  if (collectionData && typeof collectionData === 'object') {
    if (collectionData.url) {
      return collectionData.url;
    }
    if (collectionData.handle) {
      return '/collections/' + collectionData.handle;
    }
    if (collectionData.id) {
      const handleMatch = collectionData.id.match(/collections\/([^\/]+)/);
      if (handleMatch) {
        return '/collections/' + handleMatch[1];
      }
      return collectionData.id;
    }
  }
  return '';
}

const FS_SESSION_KEYS = ['isp_token_1', 'ISP_PRODUCTS_VIEWED_SESSION_2', 'fast-session-token', 'fs-hydrated', '__isp_cart'];

function clearFsSessionData() {
  try {
    FS_SESSION_KEYS.forEach(k => localStorage.removeItem(k));
    Object.keys(localStorage).forEach(k => {
      if (k.indexOf('isp_location_') === 0) localStorage.removeItem(k);
    });
  } catch (e) {
    console.warn('[FastSimon] Failed to clear session data:', e);
  }
}

/**
 * Executes a callback when the FastSimon SDK is ready
 * Times out after timeoutMs, clears FS session data for recovery on next page load,
 * and invokes onTimeout if provided
 * @param {Function} callback - Function to execute when FastSimon is ready
 * @param {Object} [options]
 * @param {number} [options.timeout=10000] - Maximum wait time in milliseconds
 * @param {Function} [options.onTimeout] - Called when the SDK fails to become ready
 */
export function whenFastSimonReady(callback, { timeout = 10000, onTimeout } = {}) {
  if (window.fast_simon_sdk_ready) {
    callback();
    return;
  }

  const checkInterval = setInterval(() => {
    if (window.fast_simon_sdk_ready) {
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
      callback();
    }
  }, 100);

  const timeoutId = setTimeout(() => {
    clearInterval(checkInterval);
    logFastSimonError(`SDK not ready after ${timeout}ms — callback skipped. Clearing FS session data for next page load.`);
    clearFsSessionData();
    if (onTimeout) onTimeout();
  }, timeout);
}

/**
 * Log a Fast Simon error to console and Noibu
 * @param {string} context - Description of where the error occurred
 * @param {Error|string} [error] - The error object or message
 */
export function logFastSimonError(context, error) {
  const message = `[FastSimon] ${context}`;
  if (error) {
    console.error(message, error);
  } else {
    console.error(message);
  }
  try {
    if (window.NOIBUJS?.addError) {
      const errorDetail = error ? `: ${error?.message || error}` : '';
      window.NOIBUJS.addError(new Error(`${message}${errorDetail}`));
    }
  } catch (e) {
    // Noibu not available
  }
}

/**
 * Log a fetch/cart error to console and Noibu with URL, status, and response context.
 * @param {string} context - Description of the failed operation
 * @param {Error|string} [error] - The original error
 * @param {Object} [meta] - Additional context
 * @param {string} [meta.url] - The request URL
 * @param {number} [meta.status] - HTTP status code
 * @param {Object} [meta.data] - Parsed response body (e.g. Shopify Cart API error)
 */
export function reportFetchError(context, error, meta = {}) {
  const { url, status, data } = meta;
  const parts = [`[Fetch] ${context}`];
  if (url) parts.push(`url=${url}`);
  if (status) parts.push(`status=${status}`);
  if (data?.message) parts.push(`message=${data.message}`);
  const message = parts.join(' ');
  const errorDetail = error ? `: ${error?.message || error}` : '';

  console.error(message, { error, status, url, data });

  try {
    if (window.NOIBUJS?.addError) {
      window.NOIBUJS.addError(new Error(`${message}${errorDetail}`));
    }
  } catch (_) {
    // Noibu not available
  }
}

/**
 * Convert a string to a handle
 * @param {string} str string to convert
 * @returns {string} handle
 */
export const handleize = str => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Build a product title search query
 * @param {string} productTitle - The product title to search for
 * @returns {string} - The search query (title:"escaped product title")
 */
export const buildProductTitleSearchQuery = productTitle => {
  if (!productTitle) return '';
  const escaped = String(productTitle).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `title:"${escaped}"`;
};

/**
 * Clean a string of any extra spaces or parentheses
 * @param {string} name string to clean
 * @returns {string} cleaned string
 */
export const cleanName = name => {
  return name.replace(/ *\([^)]*\) */g, '');
};

/**
 * Check if a string is trimmed
 * @param {string} str string to check
 * @returns {boolean} true if trimmed, false otherwise
 */
export const isTrimmed = str => {
  return str === str.trim();
};

/**
 * Sanitizes a string to be used as a safe JavaScript object key or Map key
 * @param {string} value - The string to sanitize
 * @returns {string} - Safe key string (lowercase, alphanumeric + underscores)
 */
export function sanitizeObjectKey(value) {
  if (!value || typeof value !== 'string') return '';

  return value
    .trim() // Remove leading/trailing whitespace
    .replace(/[^a-zA-Z0-9]/g, '_') // Replace non-alphanumeric with underscore
    .replace(/_+/g, '_') // Replace multiple underscores with single
    .replace(/^_|_$/g, '') // Remove leading/trailing underscores
    .toLowerCase();
}

/**
 * Finds the best matching image index for a variant
 * @param {Array} images - Array of image objects with node.url or node.image.url
 * @param {string} variantImage - URL of the variant image
 * @param {string} variantWidth - Width variant name (e.g., "Blue Bling")
 * @returns {number} Index of matching image, or -1 if not found
 */
export function findVariantImageIndex(images, variantImage, variantWidth) {
  if (!images || !images.length) return -1;

  // 1) Try exact image URL match first (ignoring query params)
  if (variantImage && typeof variantImage === 'string') {
    const normalize = url => {
      try {
        return url.split('?')[0].toLowerCase();
      } catch (e) {
        return (url || '').toLowerCase();
      }
    };
    const target = normalize(variantImage);
    const exactIndex = images.findIndex(element => {
      const imageUrl = normalize(element.node?.image?.url || element.node?.url || '');
      return imageUrl === target;
    });
    if (exactIndex !== -1) {
      return exactIndex;
    }
  }

  // 2) Fallback to width/option slug heuristic (e.g., for bling/width images)
  if (variantWidth && variantWidth !== '' && variantWidth !== 'undefined') {
    const widthSlug = handleize(variantWidth);
    const slugIndex = images.findIndex(element => {
      const imageUrl = (element.node?.url || element.node?.image?.url || '').toLowerCase();
      return imageUrl.includes(widthSlug);
    });
    if (slugIndex !== -1) return slugIndex;
  }

  // 3) Default to first image
  return 0;
}

/**
 * Augments history.replaceState and pushState once (idempotent), dispatching events
 * so Vue can sync. pushState is required because theme/filter code often uses pushState
 * with rebuilt query strings that drop params Fast Simon uses (e.g. compare_pid).
 */
export function augmentHistoryReplaceState() {
  if (window.__gmHistoryNavigationAugmented) {
    return;
  }
  window.__gmHistoryNavigationAugmented = true;
  const originalReplaceState = window.history.replaceState.bind(window.history);
  const originalPushState = window.history.pushState.bind(window.history);
  window.history.replaceState = function (state, title, url) {
    const result = originalReplaceState(state, title, url);
    window.dispatchEvent(new CustomEvent('replacestate', { detail: { state, title, url } }));
    return result;
  };
  window.history.pushState = function (state, title, url) {
    const result = originalPushState(state, title, url);
    window.dispatchEvent(new CustomEvent('pushstate', { detail: { state, title, url } }));
    return result;
  };
}

/**
 * Report a recommendation view to fastsimon
 * @param {array} productArray array of products
 * @param {string} widgetId widget id
 */
export function ReportRecommendationView(productArray, widgetId) {
  whenFastSimonReady(() => {
    window.FastSimonSDK.event({
      eventName: window.FastSimonEventName.RecommendationsProductsShown,
      data: {
        products: productArray,
        widget_id: widgetId
      }
    });
  });
}

/**
 * Report a colorway / sibling swatch switch to Fast Simon.
 * sourceProductID must be the originally shown product (impression), not the last previewed color.
 * @param {Object} options
 * @param {string|number} options.productID - Product displayed after the switch
 * @param {string|number} options.sourceProductID - Product originally displayed
 * @param {number} [options.position] - 1-based grid position; omit on PDP / mini PDP
 */
export function reportProductSwatchSwitch({ productID, sourceProductID, position } = {}) {
  const nextId = productID != null && productID !== '' ? String(productID) : '';
  const sourceId = sourceProductID != null && sourceProductID !== '' ? String(sourceProductID) : '';
  if (!nextId || !sourceId || nextId === sourceId) return;

  whenFastSimonReady(() => {
    if (!window.FastSimonSDK?.event) {
      logFastSimonError('ProductSwatchSwitchPerformed: FastSimonSDK.event unavailable');
      return;
    }
    // SDK enum may lag docs; string value is ProductSwatchSwitchPerformed → "productSwatchSwitch"
    const eventName = window.FastSimonEventName?.ProductSwatchSwitchPerformed || 'productSwatchSwitch';
    const data = {
      productID: nextId,
      sourceProductID: sourceId
    };
    const pos = Number(position);
    if (Number.isFinite(pos) && pos > 0) {
      data.position = pos;
    }
    window.FastSimonSDK.event({
      eventName,
      data
    });
  });
}

/**
 * Track viewport impression for a product
 * @param {Object} product - The product object
 * @param {number} index - The index of the product
 * @param {number} [retries=0] - Current retry count (max 10)
 */
export function trackFSViewportImpression(product, index, retries = 0) {
  const container = document.getElementById(`pid-${product}`);
  if (!container) {
    if (retries < 10) {
      setTimeout(() => {
        trackFSViewportImpression(product, index, retries + 1);
      }, 1000);
    } else {
      console.warn(`[FastSimon] trackViewportImpression: element #pid-${product} not found after ${retries} retries`);
    }
    return;
  }
  window.FastSimonSDK.trackViewportImpression(container, product, index + 1);
}

/**
 * Build sized image URL - matches existing codebase pattern
 * @param {string} url - Base image URL
 * @param {number} width - Desired width
 * @returns {string} - Sized image URL
 */
export function buildSizedImageUrl(url, width) {
  if (!url || !width) return url;
  const hasQuery = url.includes('?');
  const separator = hasQuery ? '&' : '?';
  return `${url}${separator}width=${width}`;
}

/**
 * Mobile PDP carousel image src (matches PDPFreescroll / preload): width query + @2x before file extension.
 * @param {string} url - Base image URL
 * @param {number} width - Logical width (e.g. pdp_mobile_image_width)
 * @returns {string}
 */
export function buildPdpMobileSlideSrc(url, width) {
  if (!url || !width) return url;
  const withWidth = buildSizedImageUrl(url, width);
  const dotPos = withWidth.lastIndexOf('.');
  if (dotPos <= 0) return withWidth;
  return `${withWidth.substring(0, dotPos)}@2x${withWidth.substring(dotPos)}`;
}

/**
 * Build responsive srcset with 1x and 2x versions (simple, performant)
 * @param {string} url - Base image URL
 * @param {number} baseWidth - Base width for 1x version
 * @returns {string} - Simple srcset string
 */
export function buildResponsiveSrcset(url, baseWidth) {
  if (!url || !baseWidth) return '';
  const oneX = buildSizedImageUrl(url, baseWidth);
  const twoX = buildSizedImageUrl(url, baseWidth * 2);
  return `${oneX} 1x, ${twoX} 2x`;
}

/**
 * Fetch ratings for a list of product handles
 * @param {Object} storefront - The storefront object
 * @param {Object} queries - The queries object
 * @param {Array} handles - The list of product handles
 * @param {string} countryIso2 - The country ISO 2 code
 * @returns {Map} - A map of product handles to their ratings
 */
export async function fetchRatingsForHandles(storefront, queries, handles, countryIso2) {
  const unique = Array.from(new Set((handles || []).filter(Boolean)));
  const identifiers = [
    { namespace: 'reviews', key: 'rating' },
    { namespace: 'reviews', key: 'rating_count' }
  ];
  const toNum = v => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const parse = (raw, keys) => {
    const direct = toNum(raw);
    if (direct != null) return direct;
    try {
      raw = JSON.parse(raw);
    } catch (e) {}
    if (typeof raw === 'string') {
      const n2 = toNum(raw);
      if (n2 != null) return n2;
      try {
        raw = JSON.parse(raw);
      } catch (e) {}
    }
    const candidates = [raw, raw?.value];
    for (const k of keys) {
      candidates.push(raw?.[k], raw?.value?.[k], raw?.[k]?.value);
    }
    for (const c of candidates) {
      const n = toNum(c);
      if (n != null) return n;
    }
    return null;
  };
  const results = await Promise.all(
    unique.map(async handle => {
      try {
        const resp = await storefront.request(queries.metafieldsByProductHandle, {
          variables: { handle, countryIso2, metaIdentifier: identifiers }
        });
        const metafields = resp?.data?.product?.metafields || [];
        const ratingRaw = metafields.find(m => m?.namespace === 'reviews' && m?.key === 'rating')?.value || '';
        const countRaw = metafields.find(m => m?.namespace === 'reviews' && m?.key === 'rating_count')?.value || '';
        const rating = parse(ratingRaw, ['rating', 'value']) ?? 0;
        const count = parse(countRaw, ['count', 'value']);
        return [handle, { rating, count }];
      } catch (e) {
        return [handle, { rating: 0, count: null }];
      }
    })
  );
  const map = new Map();
  for (const [handle, val] of results) map.set(handle, val);
  return map;
}

/**
 * Convenience helper for Vue components: attaches viewport class to a stable host
 * element (component's parent if available, otherwise the component root).
 * @param {import('vue').ComponentPublicInstance} vm
 * @param {{ useRoot?: boolean }} [options]
 * @returns {Function} cleanup
 */
export function handleViewportSize(vm, options = {}) {
  try {
    const host = options.useRoot ? vm?.$el : vm?.$el?.parentElement || vm?.$el;
    if (!host) return () => {};
    const update = () => {
      const width = window.innerWidth;
      const mobileMax = window.theme?.sizes?.medium;
      const tabletMax = window.theme?.sizes?.large;
      let viewportClass = 'is-desktop';
      if (width <= mobileMax) {
        viewportClass = 'is-mobile';
      } else if (width <= tabletMax) {
        viewportClass = 'is-tablet';
      }
      host.classList.remove('is-mobile', 'is-tablet', 'is-desktop');
      host.classList.add(viewportClass);
    };
    update();
    const onResize = () => update();
    document.addEventListener('theme:resize:width', onResize);
    return () => {
      document.removeEventListener('theme:resize:width', onResize);
    };
  } catch (e) {
    console.error('Error handling viewport size', e);
    return () => {};
  }
}

/**
 * Syncs scroll position across multiple elements
 * @param {HTMLElement} container - Container element to search within
 * @param {string} selector - CSS selector for scrollable elements
 * @param {string} direction - 'horizontal' or 'vertical'
 * @returns {Function} cleanup
 */
export function syncScroll(container, selector, direction = 'horizontal') {
  if (!container || !selector) return () => {};

  const scrollProp = direction === 'horizontal' ? 'scrollLeft' : 'scrollTop';
  const sizeProp = direction === 'horizontal' ? 'scrollWidth' : 'scrollHeight';
  const clientProp = direction === 'horizontal' ? 'clientWidth' : 'clientHeight';
  const busy = new WeakSet();
  let els = [];
  let rafPending = false;
  let lastSyncPos = new Map();

  const sync = ({ target }) => {
    const source = els.includes(target) ? target : target.closest?.(selector);
    if (!source || !els.includes(source) || busy.has(source)) return;

    const pos = source[scrollProp];
    const lastPos = lastSyncPos.get(source) || pos;
    const delta = Math.abs(pos - lastPos);
    lastSyncPos.set(source, pos);

    if (rafPending) return;

    rafPending = true;
    requestAnimationFrame(() => {
      for (let i = 0, n = els.length; i < n; i++) {
        const el = els[i];
        if (el === source || busy.has(el)) continue;
        busy.add(el);
        const max = el[sizeProp] - el[clientProp];
        el[scrollProp] = pos > max ? max : pos;
        requestAnimationFrame(() => busy.delete(el));
      }
      rafPending = false;
    });
  };

  const init = keepPos => {
    const saved = keepPos && els[0] ? els[0][scrollProp] : 0;
    els.forEach(el => {
      el.removeEventListener('scroll', sync);
    });
    els = Array.from(container.querySelectorAll(selector)).filter(el => el[sizeProp] > el[clientProp]);
    els.forEach(el => {
      if (direction === 'horizontal') {
        el.dataset.horizontalScroll = '';
        window.theme?.setupHorizontalScroll?.(el);
      }
      el.addEventListener('scroll', sync, { passive: true });
    });
    if (saved) {
      els.forEach(el => {
        const max = el[sizeProp] - el[clientProp];
        el[scrollProp] = saved > max ? max : saved;
      });
    }
  };

  const resize = () => requestAnimationFrame(() => init(true));
  init();
  document.addEventListener('theme:resize:width', resize);

  return () => {
    els.forEach(el => {
      el.removeEventListener('scroll', sync);
    });
    document.removeEventListener('theme:resize:width', resize);
    els = [];
  };
}
